import { isTokenExpired, useAuthStore, type AuthTokens } from '../stores/authStore.js';
import { getCurrentUser, signOut } from '../utils/firebase.js';

/**
 * Refresh the authentication tokens using Firebase
 * @returns New auth tokens if successful, null otherwise
 */
export const refreshTokens = async (): Promise<AuthTokens | null> => {
  try {
    const user = getCurrentUser();

    if (!user) return null;

    // Get fresh ID token with force refresh
    const idToken = await user.getIdToken(true);

    // Create new tokens object
    const newTokens: AuthTokens = {
      accessToken: idToken,
      idToken: idToken,
      refreshToken: user.refreshToken,
      expiresAt: Date.now() + 3600 * 1000, // tokens valid for 1 hour
    };

    // Update tokens in the store and mark as initialized
    // (setTokens resets isInitialized to false, so we must set it back to true)
    useAuthStore.getState().setTokens(newTokens);
    useAuthStore.getState().setInitialized(true);

    return newTokens;
  } catch (error) {
    console.error('Failed to refresh tokens:', error);
    return null;
  }
};

/**
 * Ensure we have valid tokens, refreshing if necessary
 * @returns Valid tokens if available, null otherwise
 */
export const ensureValidTokens = async (): Promise<AuthTokens | null> => {
  const { tokens } = useAuthStore.getState();

  if (!tokens) return null;

  // Check if tokens are still valid (not expired or about to expire)
  if (!isTokenExpired(tokens.expiresAt)) {
    return tokens;
  }

  // If tokens are expired or about to expire, try to refresh
  return await refreshTokens();
};

/**
 * Log the user out by clearing auth state and signing out from Firebase
 */
export const logout = async (): Promise<void> => {
  // Sign out from Firebase
  await signOut();
};
